import React, { useMemo, useState } from "react";
import { cx } from "../utils";

const ScoreChevron = ({ expanded }) => (
  <svg
    className={cx(
      "cc-score-band__chevron",
      expanded && "cc-score-band__chevron--expanded",
    )}
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CcScoreBand = ({
  score,
  title,
  bands,
  activeBand,
  className,
  variant = "default",
  collapsible = false,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const canCollapse = collapsible && bands.length > 0;

  const activeBandData = useMemo(
    () => bands.find((band) => band.key === activeBand),
    [bands, activeBand],
  );

  if (variant === "compact") {
    const isCollapsed = canCollapse && !expanded;

    return (
      <section
        className={cx(
          "cc-score-band",
          "cc-score-band--compact",
          isCollapsed && "is-collapsed",
          expanded && "is-expanded",
          className,
        )}
      >
        <button
          type="button"
          className="cc-score-band__compact-bar"
          onClick={canCollapse ? () => setExpanded((prev) => !prev) : undefined}
          aria-expanded={canCollapse ? expanded : undefined}
          disabled={!canCollapse}
        >
          <div className="cc-score-band__compact-meta">
            <span className="cc-score-band__eyebrow">Current score</span>
            <span className="cc-score-band__title">{title || "—"}</span>
            {isCollapsed && activeBandData?.range ? (
              <span className="cc-score-band__inline-range">
                {activeBandData.range}
              </span>
            ) : null}
          </div>
          <div className="cc-score-band__compact-trailing">
            <div className="cc-score-band__value" aria-label={`Score ${score}`}>
              {score}
            </div>
            {canCollapse ? <ScoreChevron expanded={expanded} /> : null}
          </div>
        </button>
        {canCollapse && expanded ? (
          <div className="cc-score-band__compact-bands" role="list">
            {bands.map((band) => (
              <div
                className={cx(
                  "cc-score-band__chip",
                  band.key === activeBand && "is-active",
                )}
                key={band.key}
                role="listitem"
                title={band.label}
              >
                <span className="cc-score-band__chip-label">{band.label}</span>
                <span className="cc-score-band__chip-range">{band.range}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className={cx("cc-score-band", className)}>
      <div className="cc-score-band__current">
        <div className="cc-score-band__summary">
          <div className="cc-score-band__eyebrow">Current score</div>
          <div className="cc-score-band__title">{title || "—"}</div>
        </div>
        <div className="cc-score-band__value">{score}</div>
      </div>
      {bands.map((band) => (
        <div
          className={cx(
            "cc-score-band__row",
            band.key === activeBand && "is-active",
          )}
          key={band.key}
        >
          <span className="cc-score-band__row-label">{band.label}</span>
          <span className="cc-score-band__range">{band.range}</span>
        </div>
      ))}
    </section>
  );
};

export default CcScoreBand;
